import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useLanguage } from "@/contexts/LanguageContext";
import { blogService } from "@/lib/blogService";
import type { BlogPost } from "@/types/blog";
import Navigation from "@/layout/navigation";
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function BlogPage() {
  const { language } = useLanguage();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const heroRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const publishedPosts = await blogService.getPublishedPosts();
        setPosts(publishedPosts);
      } catch (err) {
        console.error("Error loading posts:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  useLayoutEffect(() => {
    if (loading || !posts) return;

    // Initial animations
    gsap.set([titleRef.current, descriptionRef.current], {
      opacity: 0,
      y: 30,
    });

    const tl = gsap.timeline({ delay: 0.1 });
    tl.to(titleRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out",
    }).to(
      descriptionRef.current,
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
      },
      "-=0.3"
    );

    // Cards animation
    const cards = cardsRef.current?.querySelectorAll(".blog-card");
    if (cards && cards.length > 0) {
      gsap.fromTo(
        cards,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: cardsRef.current,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [loading, posts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white from-20% to-blue-300 flex items-center justify-center">
        <div className="text-lg text-slate-700">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <header
        ref={heroRef}
        className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center overflow-hidden px-8 xl:px-0 relative"
      >
        <Navigation noGradient />
        <div className="flex flex-col items-center justify-center gap-4 mt-6 md:mt-10 mb-20 z-10 max-w-4xl mx-auto">
          <h1
            ref={titleRef}
            className="text-4xl md:text-5xl font-medium text-center leading-tight"
          >
            {language === "en" ? "Blog" : "Blog"}
          </h1>
          <p
            ref={descriptionRef}
            className="text-center text-md text-slate-700 max-w-2xl"
          >
            {language === "en"
              ? "Insights, tips, and stories about nutrition, health, and wellness"
              : "Découvrez nos conseils, astuces et histoires sur la nutrition, la santé et le bien-être"}
          </p>
        </div>
      </header>

      <main className="mt-8 md:mt-16">
        <div className="max-w-6xl mx-auto">
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-700 text-lg">
                {language === "en"
                  ? "No posts available yet."
                  : "Aucun article disponible pour le moment."}
              </p>
            </div>
          ) : (
            <div
              ref={cardsRef}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-8 md:px-0"
            >
              {posts.map((post) => {
                const slug =
                  typeof post.slug === "string"
                    ? post.slug
                    : post.slug[language];
                const href =
                  language === "fr" ? `/fr/blog/${slug}` : `/blog/${slug}`;
                return (
                  <Link key={post.id} href={href} className="blog-card group">
                    <div className="border border-slate-200 hover:border-slate-300 rounded-3xl overflow-hidden h-full flex flex-col transition-all duration-500 hover:shadow-xl hover:shadow-gray-50">
                      {post.thumbnailUrl ? (
                        <div className="relative h-48 w-full overflow-hidden">
                          <Image
                            src={post.thumbnailUrl}
                            alt={post.title[language]}
                            fill
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            className="object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </div>
                      ) : (
                        <div className="h-48 w-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                          <span className="text-white text-4xl font-bold opacity-50">
                            {post.title[language].charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="p-6 flex flex-col flex-1">
                        <h2 className="text-xl font-bold mb-1  transition-colors line-clamp-2">
                          {post.title[language]}
                        </h2>
                        <p className="text-slate-400 text-sm flex-1 line-clamp-3 !p-0 !m-0">
                          {post.excerpt?.[language] || ""}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
